import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";
import { clearAuthToken } from "../utils/auth";
import "./Login.css";

const PROD_API_URL = "https://theqahubweb-production.up.railway.app";
const ENV_API_URL = (process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");
const CURRENT_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const NORMALIZED_ORIGIN = CURRENT_ORIGIN.includes("://www.theqahub.es")
  ? CURRENT_ORIGIN.replace("://www.", "://")
  : CURRENT_ORIGIN;
const getStoredApiUrl = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem("theqahub_api_base") || "";
  } catch {
    return "";
  }
};
const STORED_API_URL = getStoredApiUrl();
const STORAGE_KEY = "theqahub_saved_email";

function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Cargar email guardado
  const savedEmail = localStorage.getItem(STORAGE_KEY) || "";
  const [email, setEmail] = useState(savedEmail);
  const [rememberEmail, setRememberEmail] = useState(Boolean(savedEmail));

  // 3 modos: login / register / forgot
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const isRegister = mode === "register";
  const isForgot = mode === "forgot";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [nombre, setNombre] = useState("");
  const [apellidos, setApellidos] = useState("");

  // Terms checkbox (solo register)
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Honeypot anti-bots (campo oculto)
  const [hp, setHp] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetForm = ({ keepSuccess = false } = {}) => {
    setError("");
    if (!keepSuccess) setSuccess("");
    setPassword("");
    setConfirmPassword("");
    setTermsAccepted(false);
    setHp("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const doAuthRequest = async (baseUrl, endpoint, body) => {
        try {
          const res = await fetch(`${baseUrl}${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(body),
          });
          const data = await res.json().catch(() => ({}));
          return { res, data };
        } catch (error) {
          return { res: null, data: {}, error };
        }
      };

      const getApiCandidates = () => {
        const candidates = [];
        const pushUnique = (value) => {
          if (!value) return;
          const normalized = value.replace(/\/$/, "");
          if (!candidates.includes(normalized)) candidates.push(normalized);
        };
        const isQaHub = NORMALIZED_ORIGIN.includes("theqahub.es");
        const altOrigin = isQaHub
          ? (NORMALIZED_ORIGIN.includes("://www.")
              ? NORMALIZED_ORIGIN.replace("://www.", "://")
              : NORMALIZED_ORIGIN.replace("://", "://www."))
          : "";

        // Prioriza origen normalizado (apex) para evitar 405 en www
        pushUnique(NORMALIZED_ORIGIN);
        pushUnique(altOrigin);
        pushUnique(STORED_API_URL);
        pushUnique(ENV_API_URL);
        pushUnique(CURRENT_ORIGIN);
        pushUnique("https://www.theqahub.es");
        pushUnique("https://theqahub.es");
        pushUnique(PROD_API_URL);
        return candidates;
      };

      const isValidAuthResponse = (endpoint, res, data) => {
        if (!res?.ok) return false;
        if (endpoint.includes("forgot-password")) return true;
        return data?.ok === true || Boolean(data?.token);
      };

      const tryAuth = async (endpoint, body) => {
        const bases = getApiCandidates();
        let last = null;
        for (const base of bases) {
          const result = await doAuthRequest(base, endpoint, body);
          if (!result.res) {
            last = { ...result, base };
            continue;
          }
          last = { ...result, base };
          if (isValidAuthResponse(endpoint, result.res, result.data)) {
            return { ...result, base };
          }
        }
        return last;
      };
      if (!email) {
        setError(t("login.requiredEmail"));
        return;
      }

      // modo forgot: solo email
      if (isForgot) {
        const result = await tryAuth("/api/auth/forgot-password", { email, hp });
        if (result?.res?.ok && result.base) {
          try {
            window.localStorage.setItem("theqahub_api_base", result.base);
          } catch {}
        }

        // Siempre mensaje genérico por seguridad
        setSuccess(t("login.resetSent"));
        return;
      }

      // login/register: email + password
      if (!password) {
        setError(t("login.requiredPassword"));
        return;
      }

      if (isRegister) {
        if (!nombre || !apellidos) {
          setError(t("login.requiredName"));
          return;
        }
        if (password.length < 8) {
          setError(t("login.passwordMin"));
          return;
        }
        if (password !== confirmPassword) {
          setError(t("login.passwordMismatch"));
          return;
        }
        if (!termsAccepted) {
          setError(t("login.termsRequired"));
          return;
        }
      }

      const endpoint = isRegister ? "/api/auth/register" : "/api/auth/login";

      const payload = isRegister
        ? { email, password, nombre, apellidos, termsAccepted, hp }
        : { email, password, hp };

      const result = await tryAuth(endpoint, payload);

      const res = result?.res;
      const data = result?.data || {};
      const baseUsed = result?.base || "";

      console.log("STATUS:", res.status);
      console.log("DATA:", data);
      console.log("RES OK:", res.ok);

      if (!res) {
        setError(t("login.networkError"));
        return;
      }

      if (!res.ok) {
        setError(data.message || t("login.genericError"));
        return;     
      }

      if (!data?.ok) {
        setError(t("login.genericError"));
        return;
      }

      try {
        if (baseUsed) window.localStorage.setItem("theqahub_api_base", baseUsed);
      } catch {}

      clearAuthToken();

      // guardar email si checkbox marcado
      if (rememberEmail) localStorage.setItem(STORAGE_KEY, email);
      else localStorage.removeItem(STORAGE_KEY);

      if (isRegister) {
        setSuccess(t("login.registerSuccess"));
        setMode("login");
        resetForm({ keepSuccess: true });
        return;
      }

      console.log("Login correcto, navegando a /dashboard");
      navigate("/dashboard");
    } catch (err) {
      setError(t("login.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const goRegister = () => {
    resetForm();
    setMode("register");
  };

  const goLogin = () => {
    resetForm();
    setMode("login");
  };

  const goForgot = () => {
    resetForm();
    setMode("forgot");
  };

  return (
    <div className="login-container">
      <h1>
        {isRegister ? t("login.titleRegister") : isForgot ? t("login.titleForgot") : t("login.titleLogin")}
      </h1>

      <form onSubmit={onSubmit} className="login-form">
        {/* honeypot (oculto) */}
        <input
          type="text"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          autoComplete="off"
          tabIndex="-1"
          style={{ position: "absolute", left: "-9999px" }}
          aria-hidden="true"
        />

        {isRegister && (
          <>
            <input
              type="text"
              placeholder={t("login.namePlaceholder")}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
            />
            <input
              type="text"
              placeholder={t("login.lastNamePlaceholder")}
              value={apellidos}
              onChange={(e) => setApellidos(e.target.value)}
              required
            />
          </>
        )}

        <input
          type="email"
          placeholder={t("login.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        {!isForgot && (
          <>
            <input
              type="password"
              placeholder={t("login.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            {isRegister && (
              <input
                type="password"
                placeholder={t("login.repeatPasswordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            )}
          </>
        )}

        {/* Recordar email (vale para login y forgot también) */}
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={rememberEmail}
            onChange={(e) => setRememberEmail(e.target.checked)}
          />
          {t("login.rememberEmail")}
        </label>

        {/* Términos (solo register) */}
        {isRegister && (
          <div className="terms-checkbox">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
            />
            {t("login.termsPrefix")}{" "}
            <a className="terms-link" href="/legal-notice" target="_blank" rel="noreferrer">
                {t("login.termsService")}
            </a>{" "}
            {t("login.termsAnd")}{" "}
            <a className="terms-link" href="/privacy-policy" target="_blank" rel="noreferrer">
                {t("login.termsPolicy")}
            </a>
            {t("login.termsSuffix")}
          </div>
        )}

        {error && <p className="error">{error}</p>}
        {success && <p className="success">{success}</p>}

        <button type="submit" disabled={loading}>
          {loading
            ? t("login.processing")
            : isForgot
            ? t("login.sendLink")
            : isRegister
            ? t("login.registerButton")
            : t("login.loginButton")}
        </button>
      </form>

      {/* Botonera inferior */}
      {mode === "login" && (
        <div className="register-forgot-password">
          <button type="button" className="link-button" onClick={goRegister} disabled={loading}>
            {t("login.noAccount")}
          </button>
          <button type="button" className="link-button" onClick={goForgot} disabled={loading}>
            {t("login.forgotPassword")}
          </button>
        </div>
      )}

      {mode === "register" && (
        <button type="button" className="link-button" onClick={goLogin} disabled={loading}>
          {t("login.backToLogin")}
        </button>
      )}

      {mode === "forgot" && (
        <button type="button" className="link-button" onClick={goLogin} disabled={loading}>
          {t("login.backToLoginShort")}
        </button>
      )}
    </div>
  );
}

export default Login;
